import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';

export interface ExcelExportProgressCounts {
  processed?: number;
  total?: number;
  phase?: string;
}

export interface ExcelExportProgressState {
  id: string;
  jobId?: string;
  active: boolean;
  label: string;
  detail: string;
  percent: number;
  processed?: number;
  total?: number;
  cancellable: boolean;
}

export interface ExcelExportProgressSlot {
  id: string;
  setProgress: (label: string, percent: number, counts?: ExcelExportProgressCounts) => void;
  setJobId: (jobId: string) => void;
  markDownloadComplete: (fileName?: string) => void;
  clear: () => void;
}

export function formatExcelExportProgressDetail(
  counts?: ExcelExportProgressCounts,
): string {
  const processed = Math.max(0, Math.floor(counts?.processed ?? 0));
  const total = Math.max(0, Math.floor(counts?.total ?? 0));
  if (total <= 0) {
    if (counts?.phase === 'starting') {
      return 'Loading data';
    }
    return '';
  }
  const shown = Math.min(processed, total);
  if (counts?.phase === 'finalize') {
    return 'Preparing file';
  }
  if (counts?.phase === 'upload') {
    return 'Uploading';
  }
  return `${shown} of ${total}`;
}

@Injectable({
  providedIn: 'root',
})
export class ExcelExportProgressService {
  private readonly state = new BehaviorSubject<ExcelExportProgressState[]>([]);
  private readonly exportPollsBySlot = new Map<string, Subscription>();
  private readonly cancelHandlersBySlot = new Map<string, () => void>();

  readonly state$ = this.state.asObservable();

  constructor(private ngZone: NgZone) {}

  tryCreateSlot(): ExcelExportProgressSlot | null {
    const current = this.state.value;
    const activeCount = current.filter((s) => s.active).length;
    if (activeCount >= 2) {
      return null;
    }
    const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    this.ngZone.run(() => {
      this.state.next([
        ...current,
        {
          id,
          active: true,
          label: 'Starting export',
          detail: '',
          percent: 0,
          cancellable: false,
        },
      ]);
    });
    return {
      id,
      setProgress: (label: string, percent: number, counts?: ExcelExportProgressCounts) =>
        this.setProgress(id, label, percent, counts),
      setJobId: (jobId: string) => this.setJobId(id, jobId),
      markDownloadComplete: (fileName?: string) =>
        this.markDownloadComplete(id, fileName),
      clear: () => this.clear(id),
    };
  }

  registerCancelHandler(slotId: string, handler: () => void): void {
    this.cancelHandlersBySlot.set(slotId, handler);
  }

  getJobId(slotId: string): string | undefined {
    return this.state.value.find((s) => s.id === slotId)?.jobId;
  }

  cancelExport(slotId: string): void {
    this.cancelHandlersBySlot.get(slotId)?.();
  }

  /**
   * Cancels every in-flight export — sends the cancel request to the backend
   * and clears the frontend slot. Use only for actual browser tab close / F5.
   */
  cancelAllActiveExports(): void {
    for (const entry of this.state.value.filter((s) => s.active && s.jobId)) {
      this.cancelExport(entry.id);
    }
  }

  /**
   * Called when a page that started an export is destroyed. Polling and header
   * progress must keep running until the job finishes and the file downloads.
   */
  stopAllActivePolling(): void {
    // Intentionally no-op — do not unsubscribe or clear state on route change.
  }

  private setJobId(id: string, jobId: string): void {
    this.ngZone.run(() => {
      const next = (this.state.value || []).map((s) =>
        s.id === id ? { ...s, jobId, cancellable: true } : s,
      );
      this.state.next(next);
    });
  }

  private setProgress(
    id: string,
    label: string,
    percent: number,
    counts?: ExcelExportProgressCounts,
  ): void {
    const p = Math.min(100, Math.max(0, Math.round(percent)));
    const processed =
      counts?.processed != null ? Math.max(0, Math.floor(counts.processed)) : undefined;
    const total = counts?.total != null ? Math.max(0, Math.floor(counts.total)) : undefined;
    const detail = formatExcelExportProgressDetail(counts);
    this.ngZone.run(() => {
      const next = (this.state.value || []).map((s) =>
        s.id === id
          ? { ...s, active: true, label, detail, percent: p, processed, total }
          : s,
      );
      this.state.next(next);
    });
  }

  markDownloadComplete(id: string, fileName?: string): void {
    this.ngZone.run(() => {
      const next = (this.state.value || []).map((s) =>
        s.id === id
          ? {
              ...s,
              active: true,
              label: 'Download complete',
              detail: fileName?.trim() || '',
              percent: 100,
              cancellable: false,
            }
          : s,
      );
      this.state.next(next);
    });
  }

  private clear(id: string): void {
    this.ngZone.run(() => {
      const next = (this.state.value || []).filter((s) => s.id !== id);
      this.state.next(next);
    });
    this.cancelHandlersBySlot.delete(id);
    this.cancelPolling(id);
  }

  runExport(
    slot: ExcelExportProgressSlot,
    source$: Observable<void>,
    callbacks?: { next?: () => void; error?: (err: unknown) => void },
  ): void {
    if (!slot?.id) {
      callbacks?.error?.(new Error('Export slot is required'));
      return;
    }
    const existing = this.exportPollsBySlot.get(slot.id);
    existing?.unsubscribe();
    const sub = source$.subscribe({
      next: () => callbacks?.next?.(),
      error: (err) => {
        callbacks?.error?.(err);
        this.exportPollsBySlot.delete(slot.id);
        slot.clear();
      },
      complete: () => {
        this.exportPollsBySlot.delete(slot.id);
        window.setTimeout(() => slot.clear(), 2_500);
      },
    });
    this.exportPollsBySlot.set(slot.id, sub);
  }

  cancelPolling(slotId: string): void {
    const sub = this.exportPollsBySlot.get(slotId);
    sub?.unsubscribe();
    this.exportPollsBySlot.delete(slotId);
  }

  runBlobExport(
    slot: ExcelExportProgressSlot,
    source$: Observable<Blob>,
    options: {
      label: string;
      fileName: string;
      onComplete?: () => void;
      onError?: () => void;
    },
  ): void {
    if (!slot?.id) {
      options.onError?.();
      return;
    }
    const existing = this.exportPollsBySlot.get(slot.id);
    existing?.unsubscribe();
    slot.setProgress(options.label, 12);
    const sub = source$.subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = options.fileName;
        document.body.appendChild(a);
        a.click();
        window.setTimeout(() => {
          window.URL.revokeObjectURL(url);
          a.remove();
        }, 1500);
        options.onComplete?.();
        this.exportPollsBySlot.delete(slot.id);
        slot.clear();
      },
      error: () => {
        options.onError?.();
        this.exportPollsBySlot.delete(slot.id);
        slot.clear();
      },
    });
    this.exportPollsBySlot.set(slot.id, sub);
  }
}
